import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { paymentLabel } from "../lib/payments";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  History,
  Calendar as CalendarIcon,
  Search,
  Receipt,
  TrendingUp,
  ShoppingBag,
  Euro,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Download,
  FileSpreadsheet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const UNIT_LABELS = {
  piece: "pcs",
  litre: "L",
  kg: "kg"
};

export default function SalesHistory() {
  const { getAuthHeaders } = useAuth();
  const devise = (() => { try { return JSON.parse(localStorage.getItem("boutique") || "{}").currencySymbol || "€"; } catch { return "€"; } })();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [todaySummary, setTodaySummary] = useState(null);
  const [dailySummary, setDailySummary] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      let url = `${API}/reports/sales-history`;
      const params = new URLSearchParams();
      if (startDate) {
        params.append("start_date", format(startDate, "yyyy-MM-dd"));
      }
      if (endDate) {
        params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const [historyRes, todayRes, dailyRes] = await Promise.all([
        axios.get(url, getAuthHeaders()),
        axios.get(`${API}/reports/today-summary`, getAuthHeaders()),
        axios.get(`${API}/reports/daily-summary?days=7`, getAuthHeaders())
      ]);

      setInvoices(historyRes.data.invoices);
      setTotalCount(historyRes.data.total_count);
      setTotalRevenue(historyRes.data.total_revenue);
      setTodaySummary(todayRes.data);
      setDailySummary(dailyRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const formatDateTime = (dateStr) => {
    return format(new Date(dateStr), "dd MMM yyyy à HH:mm", { locale: fr });
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "EEEE dd MMMM", { locale: fr });
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const exportToCSV = () => {
    if (invoices.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    // Create CSV content
    const headers = [
      "Numéro facture",
      "Date",
      "Heure",
      "Produit",
      "Code-barres",
      "Quantité",
      "Unité",
      "Prix unitaire",
      "Total ligne",
      "Total facture",
      "Mode paiement",
      "Email client"
    ];

    const rows = [];
    invoices.forEach(invoice => {
      const invoiceDate = new Date(invoice.created_at);
      const dateStr = format(invoiceDate, "dd/MM/yyyy");
      const timeStr = format(invoiceDate, "HH:mm");
      
      invoice.items.forEach((item, index) => {
        rows.push([
          index === 0 ? invoice.invoice_number : "",
          index === 0 ? dateStr : "",
          index === 0 ? timeStr : "",
          item.product_name,
          item.barcode,
          item.quantity,
          UNIT_LABELS[item.unit] || item.unit,
          item.unit_price.toFixed(2),
          item.total.toFixed(2),
          index === 0 ? invoice.total.toFixed(2) : "",
          index === 0 ? paymentLabel(invoice) : "",
          index === 0 ? (invoice.customer_email || "") : ""
        ]);
      });
    });

    // Build CSV string
    const csvContent = [
      headers.join(";"),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(";"))
    ].join("\n");

    // Add BOM for Excel UTF-8 compatibility
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    // Generate filename with date range
    let filename = "ventes";
    if (startDate) {
      filename += `_du_${format(startDate, "dd-MM-yyyy")}`;
    }
    if (endDate) {
      filename += `_au_${format(endDate, "dd-MM-yyyy")}`;
    }
    if (!startDate && !endDate) {
      filename += `_${format(new Date(), "dd-MM-yyyy")}`;
    }
    filename += ".csv";
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Export réussi: ${invoices.length} facture(s)`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full tropical-gradient"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit text-foreground">Historique des ventes</h1>
          <p className="text-muted-foreground mt-1">Consultez et analysez vos ventes</p>
        </div>
        <Button
          onClick={exportToCSV}
          disabled={invoices.length === 0}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="export-csv-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Exporter CSV
        </Button>
      </div>

      {/* Today's Summary */}
      {todaySummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="stat-card stat-card-primary" data-testid="today-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Aujourd'hui</p>
                  <p className="text-3xl font-bold mt-1">{todaySummary.total_sales.toFixed(2)} {devise}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Euro className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="today-transactions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Transactions</p>
                  <p className="text-3xl font-bold mt-1 text-foreground">{todaySummary.transaction_count}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-secondary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="today-items">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Articles vendus</p>
                  <p className="text-3xl font-bold mt-1 text-foreground">{todaySummary.items_sold}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-secondary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 7 Days Summary */}
      {dailySummary.length > 0 && (
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Résumé des 7 derniers jours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {dailySummary.slice(0, 7).map((day) => (
                <div 
                  key={day.date} 
                  className="p-3 rounded-lg bg-secondary/30 text-center"
                  data-testid={`day-summary-${day.date}`}
                >
                  <p className="text-xs text-muted-foreground capitalize">
                    {format(new Date(day.date), "EEE dd", { locale: fr })}
                  </p>
                  <p className="text-lg font-bold text-primary mt-1">
                    {day.total_sales.toFixed(0)} {devise}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {day.transaction_count} vente{day.transaction_count > 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 flex flex-col md:flex-row gap-4">
              {/* Start Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Date début</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full md:w-[200px] justify-start text-left font-normal"
                      data-testid="start-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy") : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Date fin</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full md:w-[200px] justify-start text-left font-normal"
                      data-testid="end-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy") : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {(startDate || endDate) && (
              <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
                Effacer les filtres
              </Button>
            )}
          </div>

          {/* Results summary */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 items-center">
            <Badge variant="secondary" className="text-sm">
              {totalCount} facture{totalCount > 1 ? 's' : ''}
            </Badge>
            <Badge className="bg-primary/10 text-primary text-sm">
              Total: {totalRevenue.toFixed(2)} {devise}
            </Badge>
            {/* [PAIEMENTS] Totaux par moyen de paiement */}
            {Object.entries(invoices.reduce((acc, inv) => { const k = inv.payment_method === 'cash' ? 'cash' : (inv.payment_operator || 'card'); acc[k] = acc[k] || { label: paymentLabel(inv), sum: 0 }; acc[k].sum += (inv.total || 0); return acc; }, {}))
              .sort((a, b) => b[1].sum - a[1].sum)
              .map(([key, { label, sum }]) => (
                <Badge key={key} variant="outline" className="text-xs" data-testid={`payment-total-${key}`}>
                  {label} : {sum.toFixed(2)} {devise}
                </Badge>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <History className="w-8 h-8 text-secondary-foreground" />
            </div>
            <p className="text-muted-foreground">Aucune vente trouvée</p>
            <p className="text-sm text-muted-foreground mt-1">
              {startDate || endDate ? "Essayez de modifier les filtres" : "Les ventes apparaîtront ici"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <Collapsible
              key={invoice.id}
              open={expandedInvoice === invoice.id}
              onOpenChange={(open) => setExpandedInvoice(open ? invoice.id : null)}
            >
              <Card 
                className="border-border/50 overflow-hidden"
                data-testid={`invoice-${invoice.id}`}
              >
                <CollapsibleTrigger asChild>
                  <CardContent className="p-4 cursor-pointer hover:bg-accent/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Receipt className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDateTime(invoice.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-primary">{invoice.total.toFixed(2)} {devise}</p>
                          <p className="text-xs text-muted-foreground">
                            {invoice.items.length} article{invoice.items.length > 1 ? 's' : ''}
                          </p>
                        </div>
                        {expandedInvoice === invoice.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t px-4 pb-4 pt-3 bg-secondary/20">
                    {/* Items */}
                    <div className="space-y-2">
                      {invoice.items.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex justify-between items-center py-2 border-b border-border/50 last:border-0"
                        >
                          <div>
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">{item.barcode}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">
                              {item.quantity} {UNIT_LABELS[item.unit]} × {item.unit_price.toFixed(2)} {devise}
                            </p>
                            <p className="font-medium text-sm">{item.total.toFixed(2)} €</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer info */}
                    <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 items-center justify-between">
                      <div className="flex gap-2">
                        <Badge variant="outline">
                          {paymentLabel(invoice)}
                        </Badge>
                        {invoice.customer_email && (
                          <Badge variant="outline" className="text-xs">
                            {invoice.customer_email}
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/invoice/${invoice.id}`, '_blank')}
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Voir facture
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
}
